/* ================================================================
   BOM Derivation
   ----------------------------------------------------------------
   Walks renderNodes (post-solve) and groups them into BomLines by
   (materialKey, specification-string). Uses the material library
   to compute weight / cost.

   Keep in sync with the server BOM shape so the same object can be
   submitted back on `designApi.generatePackage`.
   ================================================================ */

import type {
  ProjectModel, RenderNode, BomLine, BomSummary, SideId,
} from '../model/types';
import { resolveMaterial } from '../materials';

// --------------- Internal bucket ---------------

interface BomBucket {
  materialKey: number;
  materialName: string;
  spec: string;
  count: number;
  unitAreaM2: number;
  unitVolumeM3: number;
  sides: Set<SideId>;
}

function roundMm(v: number): number {
  return Math.round(v * 1000); // mm
}

/**
 * Canonical spec string 规整后作为聚合 key。
 * 顺序保持 thickness × width × length 便于阅读。
 */
function canonicalSpec(node: RenderNode): string {
  // geometry dimensions are already in mm
  const w = Math.round(node.geometry.width);
  const h = Math.round(node.geometry.height);
  const d = Math.round(node.geometry.depth);
  // Put the smallest first (thickness), then middle, then largest (length).
  const sorted = [w, h, d].sort((a, b) => a - b);
  return `${sorted[0]}×${sorted[1]}×${sorted[2]}`;
}

function nodeVolumeM3(node: RenderNode): number {
  // geometry dimensions are already in mm
  const wMm = node.geometry.width;
  const hMm = node.geometry.height;
  const dMm = node.geometry.depth;
  return (wMm * hMm * dMm) / 1e9; // mm³ → m³
}

/** 单件展开面积：最小边作厚度，另两边相乘 → m² */
function nodeAreaM2(node: RenderNode): number {
  const sorted = [node.geometry.width, node.geometry.height, node.geometry.depth].sort((a, b) => a - b);
  // sorted[0] = thickness; sorted[1] × sorted[2] = 板/木方主面 mm²
  return (sorted[1] * sorted[2]) / 1e6; // mm² → m²
}

// --------------- Entry point ---------------

export function calculateBom(
  project: ProjectModel,
  renderNodes: RenderNode[],
): BomSummary {
  const buckets = new Map<string, BomBucket>();

  for (const node of renderNodes) {
    const key = `${node.material}|${canonicalSpec(node)}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      const material = resolveMaterial(project, node.material);
      bucket = {
        materialKey: node.material,
        materialName: material.name,
        spec: canonicalSpec(node),
        count: 0,
        unitAreaM2: nodeAreaM2(node),
        unitVolumeM3: nodeVolumeM3(node),
        sides: new Set(),
      };
      buckets.set(key, bucket);
    }
    bucket.count += 1;
    bucket.sides.add(node.sideId);
  }

  let totalVolume = 0;
  let totalWeight = 0;
  let totalCost = 0;
  const lines: BomLine[] = [];

  for (const b of buckets.values()) {
    const material = resolveMaterial(project, b.materialKey);
    const lineVolume = b.unitVolumeM3 * b.count;
    const lineWeight = lineVolume * material.densityKgPerM3;
    const lineCost = lineVolume * material.pricePerM3;

    totalVolume += lineVolume;
    totalWeight += lineWeight;
    totalCost += lineCost;

    lines.push({
      materialKey: b.materialKey,
      materialName: b.materialName,
      spec: b.spec,
      count: b.count,
      unitAreaM2: b.unitAreaM2,
      unitVolumeM3: b.unitVolumeM3,
      totalAreaM2: b.unitAreaM2 * b.count,
      totalVolumeM3: lineVolume,
      totalWeightKg: lineWeight,
      totalCostYuan: lineCost,
      fromSides: [...b.sides],
    });
  }

  // Sort by cost descending so designer sees high-impact lines first
  lines.sort((a, b) => b.totalCostYuan - a.totalCostYuan);

  return {
    lines,
    totalVolumeM3: totalVolume,
    totalWeightKg: totalWeight,
    totalCostYuan: totalCost,
    meshCount: renderNodes.length,
  };
}
